import { type Column, type ColumnDef } from "@tanstack/react-table";
import { type TFunction } from "i18next";

import { EMPTY_VALUE } from "./constants";
import { type AdministrativeAreaTypeColumnMeta } from "./types";
import { type AdministrativeAreaType } from "../../types";

export const getColumnMeta = (columnDef: ColumnDef<AdministrativeAreaType, unknown>) =>
  columnDef.meta as AdministrativeAreaTypeColumnMeta | undefined;

export const getEntityKey = (value: AdministrativeAreaType["id"]) =>
  value === undefined || value === null ? undefined : String(value);

export const getRowId = (row: AdministrativeAreaType, index: number) =>
  getEntityKey(row.id) ?? `${index}-${row.name ?? "administrative-area-type"}`;

export const getName = (areaType: AdministrativeAreaType) => areaType.name?.trim() || "";

export const getTimestamp = (value: string | null | undefined) => {
  if (!value) {
    return undefined;
  }

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? undefined : timestamp;
};

// Falls back to the area type's default name when untranslated for the given language.
export const getTranslatedName = (areaType: AdministrativeAreaType, languageCode: string) =>
  areaType.name_translations?.[languageCode]?.trim() || getName(areaType);

export const createParentLookup = (administrativeAreaTypes: AdministrativeAreaType[]) =>
  new Map(
    administrativeAreaTypes.flatMap(areaType => {
      const id = getEntityKey(areaType.id);

      return id ? [[id, areaType] as const] : [];
    })
  );

export const getParentName = (
  areaType: AdministrativeAreaType,
  parentLookup: Map<string, AdministrativeAreaType>,
  t: TFunction
) => {
  if (areaType.parent === undefined || areaType.parent === null) {
    return t("administrativeAreaTypes.noParent");
  }

  return getName(parentLookup.get(String(areaType.parent)) ?? {}) || EMPTY_VALUE;
};

export const getColumnSortAria = (column: Column<AdministrativeAreaType, unknown>) => {
  const sortDirection = column.getIsSorted();

  if (sortDirection === "asc") {
    return "ascending";
  }

  if (sortDirection === "desc") {
    return "descending";
  }

  return undefined;
};
